<template>
  <div
    class="appointments"
    v-if="combinedFutureAppointments?.length || combinedPastAppointments?.length"
  >
    <hr />
    <h3 class="appointments__title" v-if="combinedFutureAppointments?.length">
      {{
        combinedFutureAppointments.length > 1 ? 'Ihre kommenden Termine' : 'Ihr kommender Termin'
      }}
    </h3>
    <template v-for="(future, index) in combinedFutureAppointments" :key="future.id">
      <!-- TVG Appointments -->
      <Selection
        v-if="future.snrIds"
        :id="future.id"
        :title="formatDateTime('day, dateShort', future.von) + ' • ' + future.snrBezeichnung"
        :subtitle="
          future.dienststelleModel.bezeichnung +
          ' • ' +
          future.terminFahrzeugModel.marke +
          ' ' +
          future.terminFahrzeugModel.typ
        "
        :divider="index !== combinedFutureAppointments.length - 1"
        :open="future.id === +$route.params.appointmentId"
      >
        <template v-slot:icon>
          <IconCalendar aria-hidden="true" />
        </template>
        <template v-slot:expansion>
          <div class="appointments__expansion">
            <div class="appointments__expansion__info">
              <p>Angebot</p>
              <p>{{ future.snrBezeichnung }}</p>
              <p>Fahrzeug</p>
              <p>
                {{
                  future.terminFahrzeugModel.marke +
                  ' ' +
                  future.terminFahrzeugModel.typ +
                  (future.terminFahrzeugModel.kennzeichen
                    ? ` • ${future.terminFahrzeugModel.kennzeichen}`
                    : '')
                }}
              </p>
              <p>Stützpunkt</p>
              <p>
                {{
                  future.dienststelleModel.bezeichnung + ' • ' + future.dienststelleModel.adresse
                }}
              </p>
              <p>Termin</p>
              <p>
                {{
                  formatDateTime('day, dateLong • timeFrom - timeUntil Uhr', future.von, future.bis)
                }}
              </p>
              <p>Kosten</p>
              <p>{{ future.preis.toFixed(2).replace('.', ',') }} €</p>
            </div>
            <div class="appointments__expansion__actions">
              <div class="appointments__expansion__actions__buttons">
                <Button label="Termin exportieren" @clicked="exportAppointment(future.id)" />
                <Button label="Termin bearbeiten" outline @clicked="editAppointment(future)" />
              </div>
              <button
                class="link"
                @click="deleteAppointment(future.id, future.snrBezeichnung, future.von)"
              >
                Termin stornieren
              </button>
            </div>
            <hr />
          </div>
        </template>
      </Selection>
      <!-- Versicherungs-/Rechtsberatung -->
      <Selection
        v-else
        :id="future.id"
        :title="formatDateTime('day, dateShort', future.startsAt) + ' • ' + future.appointmentType"
        :subtitle="future.servicePointName + ' • ' + future.appointmentTopics.join(', ')"
        :divider="index !== combinedFutureAppointments.length - 1"
        :open="future.id === $route.params.appointmentId"
      >
        <template v-slot:icon>
          <IconCalendar aria-hidden="true" />
        </template>
        <template v-slot:expansion>
          <div class="appointments__expansion">
            <div class="appointments__expansion__info">
              <p>Angebot</p>
              <p>{{ future.appointmentType }}</p>
              <p>Themen</p>
              <p>{{ future.appointmentTopics.join(', ') }}</p>
              <p>Typ</p>
              <p>{{ future.consultationType }}</p>
              <p>Stützpunkt</p>
              <p>
                {{ future.servicePointName + ' • ' + future.servicePointFullAddress }}
              </p>
              <p>Termin</p>
              <p>
                {{
                  formatDateTime(
                    'day, dateLong • timeFrom - timeUntil Uhr',
                    future.startsAt,
                    future.endsAt
                  )
                }}
              </p>
              <template v-if="future.meetingUrl">
                <p>Meeting-Link</p>
                <p>
                  <a :href="future.meetingUrl" target="_blank"
                    >Hier klicken um Meeting beizutreten</a
                  >
                </p>
              </template>
            </div>
            <div class="appointments__expansion__actions">
              <div class="appointments__expansion__actions__buttons">
                <Button label="Termin exportieren" @clicked="exportOtbAppointment(future.id)" />
                <Button label="Termin bearbeiten" outline @clicked="editOtbAppointment(future)" />
              </div>
              <button class="link" @click="deleteOtbAppointment(future)">Termin stornieren</button>
            </div>
            <hr />
          </div>
        </template>
      </Selection>
    </template>
    <h3 class="appointments__title" v-if="combinedPastAppointments?.length">
      {{
        combinedPastAppointments.length > 1 ? 'Ihre vergangenen Termine' : 'Ihr vergangener Termin'
      }}
    </h3>
    <template v-for="(past, index) in combinedPastAppointments" :key="past.id">
      <Selection
        :id="past.id"
        v-if="past.snrIds"
        :title="formatDateTime('dateShort', past.von) + ' • ' + past.snrBezeichnung"
        :subtitle="
          past.dienststelleModel.bezeichnung +
          ' • ' +
          past.terminFahrzeugModel.marke +
          ' ' +
          past.terminFahrzeugModel.typ
        "
        :divider="index !== combinedPastAppointments.length - 1"
        disabled
      >
        <template v-slot:icon>
          <IconCalendar disabled aria-hidden="true" />
        </template>
        <template v-slot:action>
          <button class="link" @click="bookAgain(past)">Erneut buchen</button>
        </template>
      </Selection>
      <Selection
        v-else
        :id="past.id"
        :title="formatDateTime('dateShort', past.startsAt) + ' • ' + past.appointmentType"
        :subtitle="past.servicePointName + ' • ' + past.appointmentTopics.join(', ')"
        :divider="index !== combinedPastAppointments.length - 1"
        disabled
      >
        <template v-slot:icon>
          <IconCalendar disabled />
        </template>
      </Selection>
    </template>
    <el-dialog
      v-model="showDeleteDialog"
      :show-close="false"
      custom-class="confirmation"
      attach-to-body
    >
      <DeleteDialog @closeDeleteDialog="closeDialog" :dialogInfo="dialogInfo" />
    </el-dialog>
  </div>
</template>

<script>
import { convertToSlug, formatDateTime } from '@/utils/format.js'
import { mapGetters } from 'vuex'

import Button from '@/components/Button.vue'
import IconCalendar from '@/components/icons/IconCalendar.vue'
import Selection from '@/components/Selection.vue'
import { getExternalAppointmentBaseUrl, navigateTo } from '@/utils/misc.js'
/* eslint-disable-next-line */
import { OtbAppointment } from '../../types/otbBeratung'
import DeleteDialog from './DeleteDialog.vue'

export default {
  components: { IconCalendar, Button, Selection, DeleteDialog },
  mounted() {
    this.getUserAppointments()
  },
  data() {
    return {
      showDeleteDialog: false,
      dialogInfo: {
        id: null,
        services: null,
        date: null,
        time: null,
        isOtb: false, // Flag to indicate if the appointment is an OTb appointment
      },
    }
  },
  computed: {
    ...mapGetters([
      'user',
      'sessionData',
      'userAccountInfo',
      'combinedFutureAppointments', // mixed array of TVG and OTb appointments
      'combinedPastAppointments',
      'federalStates',
    ]),
  },
  watch: {
    sessionData() {
      if (this.sessionData) this.getUserAppointments()
    },
    combinedFutureAppointments() {
      // scroll to Selection with active appointment
      const activeAppointment = this.combinedFutureAppointments.find(
        //convert both to strings for comparison since the id can be a number (tvg appointment) or string (otb appointment)
        (appointment) => String(appointment.id) === String(this.$route.params.appointmentId)
      )
      if (activeAppointment) {
        this.$nextTick(() => {
          const element = document.getElementById(activeAppointment.id)
          if (element) {
            // Get element position and apply offset
            const rect = element.getBoundingClientRect()
            const offsetPosition = rect.top + window.pageYOffset - 200 // Adjust offset as needed

            // Scroll to the calculated position
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth',
            })
          }
        })
      }
    },
  },
  methods: {
    formatDateTime,
    getUserAppointments() {
      if (this.user?.isMember && this.sessionData) {
        this.$store.dispatch('getUserAppointments')
        this.$store.dispatch('getOtbAppointments')
      }
    },
    exportAppointment(id) {
      this.$store.dispatch('exportAppointment', id)
    },
    exportOtbAppointment(id) {
      this.$store.dispatch('exportOtbAppointment', id)
    },
    editAppointment(future) {
      this.populateStore(future)
      this.$store.commit('setPreviousAppointmentId', future.id)
      this.toBookingSteps(future.dienststelleModel.bundeslandId)
    },
    deleteAppointment(id, snrBezeichnung, von) {
      this.dialogInfo = {
        id: id,
        services: snrBezeichnung,
        date: this.formatDateTime('day, dateShort', von),
        time: this.formatDateTime('timeFrom', von),
      }
      this.showDeleteDialog = true
    },
    /** @param {OtbAppointment} appointment  */
    deleteOtbAppointment(appointment) {
      this.dialogInfo = {
        id: appointment.id,
        services: appointment.appointmentType,
        date: this.formatDateTime('day, dateShort', appointment.startsAt),
        time: this.formatDateTime('timeFrom', appointment.startsAt),
        isOtb: true, // Indicate this is an OTb appointment
      }
      this.showDeleteDialog = true
    },
    bookAgain(past) {
      this.populateStore(past)
      this.toBookingSteps(past.dienststelleModel.bundeslandId)
    },
    closeDialog() {
      this.showDeleteDialog = false
    },
    populateStore(data) {
      this.$store.commit('setSelectedState', {
        id: data.dienststelleModel.bundeslandId,
        landesclubId: data.dienststelleModel.landesclubId,
      })
      this.$store.commit('setSelectedServicesId', data.snrIds)
      this.$store.commit('setRebookVehicle', {
        fin: data.terminFahrzeugModel.fin,
        licensePlate: data.terminFahrzeugModel.kennzeichen,
        brand: data.terminFahrzeugModel.marke,
        model: data.terminFahrzeugModel.typ,
        fourWheelDrive: data.terminFahrzeugModel.allrad,
        gebremst: data.terminFahrzeugModel.gebremst,
        diesel: data.terminFahrzeugModel.diesel,
        firstName: this.user.firstname,
        lastName: this.user.lastname,
      })
      this.$store.commit('setActiveStationId', data.dienststelleModel.dienststelleId)
      this.$store.commit('setActiveStation', { ...data.dienststelleModel })
    },
    toBookingSteps(bundeslandId) {
      const state = this.federalStates.find((state) => state.id === bundeslandId)
      this.$router.push({
        name: 'Service',
        query: { bundesland: convertToSlug(state.bezeichnung) },
      })
      this.$store.commit('setActiveStep', 1)
    },
    /**
     * @param {OtbAppointment} appointment
     */
    editOtbAppointment(appointment) {
      navigateTo(
        getExternalAppointmentBaseUrl() +
          `/beratungstermine/${appointment.tenantId}/termin/${appointment.id}/bearbeiten`
      )
    },
  },
}
</script>

<style lang="scss">
.appointments {
  > hr {
    @include container-divider;
  }

  #content &__title,
  &__title {
    font-size: 12px;
    margin-top: 24px;
    margin-bottom: 4px;
    font-weight: $font-weight-regular;
    line-height: $line-height;
  }

  &__expansion {
    margin-top: 6px;
    margin-left: 56px;

    &__info {
      display: grid;
      grid-template-columns: max-content 1fr;
      column-gap: 32px;
      row-gap: 5px;
      margin-bottom: 24px;

      p:nth-of-type(2n + 1) {
        color: $color-text-secondary;
        font-size: 12px;
        line-height: 1.75;
      }

      p:last-of-type {
        font-weight: $font-weight-medium;
      }

      p:nth-last-of-type(2),
      p:nth-last-of-type(1) {
        margin-top: 12px;
      }
    }

    @media (max-width: $breakpoint-max-mobile) {
      margin-left: 52px;

      &__info {
        grid-template-columns: none;
        row-gap: unset;

        p:nth-of-type(2n) {
          margin-bottom: 8px;
        }

        p:nth-last-of-type(2) {
          margin-top: 8px;
        }

        p:nth-last-of-type(1) {
          margin: 0;
        }
      }
    }

    &__actions {
      @include flex-center-align;
      flex-wrap: wrap;
      gap: 16px;
      justify-content: space-between;
      margin-bottom: 24px;

      &__buttons {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
    }
  }
}
</style>
